import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import axios from 'axios';
import moment from 'moment';

import InfoPanel from '../Panels/InfoPanel';
import './style.css';
import history from '../../helpers/history';

/**
 * Dashboard widget: Suspense Account
 *
 * Replaces the old "Extra Charges Table" widget. Shows the TOTAL amount
 * currently sitting in the suspense account (only `pending` records) and a
 * table of recent transactions with their time and amount.
 *
 * The backend (suspenseController.getSuspenseRecords) already enforces
 * per-user scoping based on the JWT — admins see all suspense records,
 * landlords/agents only see records on properties they have access to. So
 * the figure here is correctly limited to the logged-in user without any
 * extra filtering on the client.
 */
// Cap at 2dp — bare toLocaleString() with no options lets binary
// floating-point noise from upstream arithmetic leak straight into the UI.
const moneyOpts = { maximumFractionDigits: 2 };

const Suspense = ({ user }) => {
  if (!user || !user.token) {
    history.push('/signin');
  }
  const userToken = user ? user.token : {};
  const requestConfig = {
    headers: {
      Authorization: `Bearer ${userToken}`,
      'Content-Type': 'application/json',
    },
  };

  const [records, setRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const isMounted = React.useRef(null);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    (async () => {
      try {
        // Only PENDING records contribute to the "in suspense" total —
        // reconciled records have already been moved to a tenant payment.
        const url = `${process.env.REACT_APP_API_URL}suspense?where={"status":"pending"}&take=500&order={"createdat":"DESC"}`;
        const {
          data: { response },
        } = await axios.get(url, requestConfig);
        if (isMounted.current) {
          setRecords(Array.isArray(response) ? response : []);
          setIsLoading(false);
        }
      } catch (e) {
        if (isMounted.current) {
          setIsLoading(false);
        }
      }
    })();
  }, []);

  const totalAmount = records.reduce(
    (sum, r) => sum + (Number(r.amount) || 0),
    0,
  );

  let component = null;
  if (isLoading) {
    component = <InfoPanel message="Loading..." />;
  } else if (records.length) {
    component = (
      <table className="charges">
        <thead>
          <tr>
            <th>Transaction Time</th>
            <th style={{ textAlign: 'right' }}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {records.map((r) => (
            <tr key={r.id}>
              <td>{moment(r.createdat).format('D/M/YYYY HH:mm')}</td>
              <td style={{ textAlign: 'right' }}>
                {`Ksh ${Number(r.amount || 0).toLocaleString(undefined, moneyOpts)}`}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  } else {
    component = <InfoPanel message="No pending suspense records" />;
  }

  return (
    <div className="col-lg-3">
      <div className="hpanel">
        <div className="panel-heading">Suspense Account</div>
        <div className="panel-body list">
          <div
            style={{
              padding: '12px 16px',
              borderBottom: '1px solid #eee',
              background: '#fafafa',
            }}
          >
            <div
              style={{
                fontSize: 11,
                color: '#888',
                textTransform: 'uppercase',
                letterSpacing: 0.5,
              }}
            >
              Total in Suspense
            </div>
            <div
              style={{
                fontSize: 20,
                fontWeight: 700,
                color: '#c0392b',
                marginTop: 4,
              }}
            >
              {`Ksh ${totalAmount.toLocaleString(undefined, moneyOpts)}`}
            </div>
          </div>
          <div className="table-responsive project-list">{component}</div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => state;

Suspense.propTypes = {
  user: PropTypes.oneOfType([PropTypes.object, PropTypes.string]).isRequired,
};

export default connect(mapStateToProps)(Suspense);
